import { useMemo, useState, type CSSProperties } from 'react';
import { X } from 'lucide-react';
import type { ComponentTemplate } from '../types';
import { useLibrary } from '../libraryStore';
import { useStore } from '../store';
import { showToast } from './Toast';
import {
  applyLibraryMerge,
  diffLibrary,
  type MergeDecisions,
} from '../libraryMerge';

/**
 * Import preview + conflict resolver. Shows what an incoming library would add,
 * what conflicts with your current parts (per-row: keep mine vs take theirs),
 * and what is identical — then merges only what you approve. Two teammates
 * sharing a library can each pull the other's changes without silently losing
 * their own edits.
 */
export function LibraryImportModal({
  incoming,
  onClose,
}: {
  incoming: ComponentTemplate[];
  onClose: () => void;
}) {
  const current = useLibrary((s) => s.templates);
  const setAll = useLibrary((s) => s.setAll);
  const syncTemplateInstances = useStore((s) => s.syncTemplateInstances);

  const diff = useMemo(() => diffLibrary(current, incoming), [current, incoming]);

  const [addKeys, setAddKeys] = useState<Set<string>>(
    () => new Set(diff.added.map((t) => t.key)),
  );
  // Default: take theirs for conflicts (import intent), but every row is flippable.
  const [takeTheirs, setTakeTheirs] = useState<Set<string>>(
    () => new Set(diff.conflicts.map((c) => c.mine.key)),
  );

  const toggleAdd = (key: string) =>
    setAddKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setConflict = (mineKey: string, theirs: boolean) =>
    setTakeTheirs((prev) => {
      const next = new Set(prev);
      if (theirs) next.add(mineKey);
      else next.delete(mineKey);
      return next;
    });

  const apply = () => {
    const decisions: MergeDecisions = { addKeys, takeTheirsForMineKey: takeTheirs };
    const merged = applyLibraryMerge(current, diff, decisions);
    setAll(merged);
    // Re-sync placed instances to the merged templates.
    let removedEdges = 0;
    for (const t of merged) removedEdges += syncTemplateInstances(t).removedEdges;
    const addedN = [...addKeys].length;
    const updatedN = [...takeTheirs].length;
    showToast(
      `Library merged · ${addedN} added · ${updatedN} updated` +
        (removedEdges > 0 ? ` · ${removedEdges} wire(s) removed` : ''),
      removedEdges > 0 ? 'info' : 'success',
    );
    onClose();
  };

  const replaceAll = () => {
    setAll(incoming);
    for (const t of incoming) syncTemplateInstances(t);
    showToast(`Library replaced · ${incoming.length} parts`, 'info');
    onClose();
  };

  const nothingToDo = diff.added.length === 0 && diff.conflicts.length === 0;

  return (
    <div style={overlay} onMouseDown={onClose}>
      <div style={panel} onMouseDown={(e) => e.stopPropagation()}>
        <div style={titleBar}>
          <span style={title}>Import library — preview</span>
          <button style={closeBtn} onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        <div style={summary}>
          {incoming.length} incoming · <b>{diff.added.length}</b> new ·{' '}
          <b>{diff.conflicts.length}</b> conflicting · {diff.unchanged.length} identical ·{' '}
          {diff.onlyMine.length} only yours (kept)
        </div>

        <div style={body}>
          {nothingToDo && (
            <div style={emptyNote}>
              Nothing to merge — the incoming library matches what you already have.
            </div>
          )}

          {diff.added.length > 0 && (
            <section>
              <div style={sectionHead}>New parts — will be added</div>
              {diff.added.map((t) => (
                <label key={t.key} style={row}>
                  <input
                    type="checkbox"
                    checked={addKeys.has(t.key)}
                    onChange={() => toggleAdd(t.key)}
                  />
                  <span style={nameCell}>{t.label || '(unnamed)'}</span>
                  <span style={metaCell}>
                    {t.category || '—'}
                    {t.partNumber ? ` · ${t.partNumber}` : ''} · {t.pins?.length ?? 0} pins
                  </span>
                </label>
              ))}
            </section>
          )}

          {diff.conflicts.length > 0 && (
            <section>
              <div style={sectionHead}>
                Conflicts — differ from your version
                <span style={{ flex: 1 }} />
                <button style={bulkBtn} onClick={() => setTakeTheirs(new Set(diff.conflicts.map((c) => c.mine.key)))}>
                  Take all theirs
                </button>
                <button style={bulkBtn} onClick={() => setTakeTheirs(new Set())}>
                  Keep all mine
                </button>
              </div>
              {diff.conflicts.map((c) => {
                const theirs = takeTheirs.has(c.mine.key);
                return (
                  <div key={c.mine.key} style={conflictRow}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={nameCell}>{c.theirs.label || c.mine.label || '(unnamed)'}</div>
                      <div style={metaCell}>{describeDiff(c.mine, c.theirs)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button
                        style={choiceBtn(!theirs)}
                        onClick={() => setConflict(c.mine.key, false)}
                      >
                        Keep mine
                      </button>
                      <button
                        style={choiceBtn(theirs)}
                        onClick={() => setConflict(c.mine.key, true)}
                      >
                        Take theirs
                      </button>
                    </div>
                  </div>
                );
              })}
            </section>
          )}
        </div>

        <div style={footer}>
          <button style={dangerBtn} onClick={replaceAll} title="Discard your library and use the imported one as-is">
            Replace entire library
          </button>
          <span style={{ flex: 1 }} />
          <button style={ghostBtn} onClick={onClose}>
            Cancel
          </button>
          <button style={primaryBtn} onClick={apply} disabled={nothingToDo}>
            Apply merge
          </button>
        </div>
      </div>
    </div>
  );
}

function describeDiff(mine: ComponentTemplate, theirs: ComponentTemplate): string {
  const parts: string[] = [];
  if ((mine.pins?.length ?? 0) !== (theirs.pins?.length ?? 0))
    parts.push(`pins ${mine.pins?.length ?? 0}→${theirs.pins?.length ?? 0}`);
  if ((mine.partNumber ?? '') !== (theirs.partNumber ?? '')) parts.push('part no.');
  if ((mine.manufacturer ?? '') !== (theirs.manufacturer ?? '')) parts.push('mfr');
  if ((mine.label ?? '') !== (theirs.label ?? '')) parts.push('label');
  if ((mine.category ?? '') !== (theirs.category ?? '')) parts.push('category');
  if ((mine.color ?? '') !== (theirs.color ?? '')) parts.push('color');
  if (JSON.stringify(mine.mechanical ?? null) !== JSON.stringify(theirs.mechanical ?? null))
    parts.push('mechanical');
  if (JSON.stringify(mine.defaultProperties ?? {}) !== JSON.stringify(theirs.defaultProperties ?? {}))
    parts.push('properties');
  return parts.length ? `differs: ${parts.join(', ')}` : 'differs';
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const panel: CSSProperties = {
  width: 640,
  maxWidth: '94vw',
  maxHeight: '86vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
};
const titleBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px 8px 14px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
};
const title: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
};
const closeBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
const summary: CSSProperties = {
  padding: '8px 14px',
  fontSize: 11.5,
  color: 'var(--text-muted)',
  background: 'var(--panel-alt)',
  borderBottom: '1px solid var(--panel-border)',
};
const body: CSSProperties = { overflowY: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 10 };
const emptyNote: CSSProperties = { padding: '26px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 };
const sectionHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 10.5,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--text-muted)',
  padding: '6px 4px',
  position: 'sticky',
  top: 0,
  background: 'var(--panel)',
};
const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 6px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};
const conflictRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 6px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--panel-border)',
  marginBottom: 4,
};
const nameCell: CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const metaCell: CSSProperties = { fontSize: 10.5, color: 'var(--text-muted)' };
const bulkBtn: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  padding: '2px 7px',
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
};
function choiceBtn(active: boolean): CSSProperties {
  return {
    border: `1px solid ${active ? 'var(--accent)' : 'var(--panel-border)'}`,
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--accent-soft)' : 'var(--panel)',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    padding: '4px 9px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  };
}
const footer: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderTop: '1px solid var(--panel-border)',
  background: 'var(--panel-alt)',
};
const ghostBtn: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '5px 11px',
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
};
const primaryBtn: CSSProperties = {
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  padding: '5px 12px',
  fontSize: 11.5,
  fontWeight: 800,
  cursor: 'pointer',
};
const dangerBtn: CSSProperties = {
  border: '1px solid var(--danger-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--danger)',
  padding: '5px 11px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};
